import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../lib/api';
import { Card } from './ui/card';
import { Stethoscope, Utensils } from 'lucide-react';
import InfoTooltip from './InfoTooltip';

/**
 * ClinicEvalStatusPanel
 * ---------------------------------------------------------------
 * At-a-glance dashboard panel showing every clinic's Speech and
 * Feeding eval "open/closed" state as a traffic-light indicator.
 * Green = open, Red = closed. Rendered near the top of the
 * Dashboard so admins can spot capacity issues in one glance.
 * Each row deep-links to that clinic's detail page.
 */
export default function ClinicEvalStatusPanel() {
  const [clinics, setClinics] = useState(null);

  useEffect(() => {
    api.get('/clinics')
      .then(r => setClinics(Array.isArray(r.data) ? r.data : []))
      .catch(() => setClinics([]));
  }, []);

  if (!clinics) return null;
  if (clinics.length === 0) return null;

  return (
    <Card className="rounded-2xl border-[#E8E5EC] p-6" data-testid="clinic-eval-status-panel">
      <div className="mb-4 flex items-baseline justify-between flex-wrap gap-2">
        <div>
          <h3 className="font-serif text-2xl text-[#2D2833] flex items-center">Clinic eval availability<InfoTooltip testId="tt-eval-availability" text="Reflects each clinic's 'Accepting new evals' toggle for Speech and Feeding, set on the Clinic detail page. Green means the clinic is currently open for new evals of that type." /></h3>
          <div className="text-xs text-[#685B72] mt-0.5">
            Which locations are currently accepting new evals. Green = open, red = closed.
          </div>
        </div>
        <div className="flex items-center gap-4 text-[11px] text-[#685B72]">
          <span className="flex items-center gap-1.5"><StatusDot open={true} /> Open</span>
          <span className="flex items-center gap-1.5"><StatusDot open={false} /> Closed</span>
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {clinics.map(c => (
          <ClinicStatusRow key={c.clinic_id || c.name} clinic={c} />
        ))}
      </div>
    </Card>
  );
}

function ClinicStatusRow({ clinic }) {
  const speechOpen = !!clinic.speech_evals_open;
  const feedingOpen = !!clinic.feeding_evals_open;
  const safeName = String(clinic.name || 'clinic').replace(/\W+/g, '_');
  return (
    <Link
      to={`/clinics/${clinic.clinic_id}`}
      className="rounded-xl border border-[#E8E5EC] bg-white p-3 flex items-center justify-between gap-3 transition-all hover:-translate-y-0.5 hover:shadow-md hover:border-[#BAA9CA]/60"
      data-testid={`clinic-eval-status-${safeName}`}
    >
      <div className="min-w-0 flex-1">
        <div className="font-serif text-base text-[#2D2833] truncate">{clinic.name}</div>
        {clinic.office_director && (
          <div className="text-[10px] text-[#685B72] truncate mt-0.5">
            OD · {clinic.office_director}
          </div>
        )}
      </div>
      <div className="flex items-center gap-3 shrink-0">
        <StatusPill
          label="Speech"
          open={speechOpen}
          icon={Stethoscope}
          testid={`clinic-eval-status-${safeName}-speech`}
        />
        <StatusPill
          label="Feeding"
          open={feedingOpen}
          icon={Utensils}
          testid={`clinic-eval-status-${safeName}-feeding`}
        />
      </div>
    </Link>
  );
}

function StatusPill({ label, open, icon: Icon, testid }) {
  const bg = open ? 'bg-[#E4F0EB]' : 'bg-[#F5EAEA]';
  const text = open ? 'text-[#839E97]' : 'text-[#8A3A3A]';
  return (
    <div
      className={`flex items-center gap-1.5 rounded-full ${bg} ${text} px-2 py-1`}
      title={`${label} evals: ${open ? 'Open' : 'Closed'}`}
      data-testid={testid}
      data-status={open ? 'open' : 'closed'}
    >
      <Icon size={12} />
      <span className="text-[10px] uppercase tracking-wide font-semibold">{label}</span>
      <StatusDot open={open} />
    </div>
  );
}

function StatusDot({ open }) {
  return (
    <span
      className={`inline-block w-2.5 h-2.5 rounded-full ${open ? 'bg-[#839E97]' : 'bg-[#8A3A3A]'}`}
      style={{
        boxShadow: open
          ? '0 0 0 2px rgba(63,143,110,0.18)'
          : '0 0 0 2px rgba(138,58,58,0.18)',
      }}
    />
  );
}
